/**
 * rationale.tsx — why a decision was made, retrieved FRESH, and rendered as four
 * separate facts rather than one narrative.
 *
 * THE REQUIREMENT, and how the structure meets it
 * -----------------------------------------------
 * A fresh browser context must be able to retrieve the selected alternative, the
 * rejected alternatives, the open assumptions, the reopening triggers, the
 * selection basis, and whether the record is draft or published. The prohibitions
 * are equally load-bearing: none of it may come from chat state, from model
 * memory, or from a fixture, and it must not require a running dialogue to
 * reconstruct.
 *
 * They are met structurally, not by promise:
 *
 *   • NO MODULE-LEVEL CACHE. Every mount issues its own request. A second mount
 *     in the same session re-requests, so a "cached" rationale can never be shown
 *     as a retrieved one, and a reviewer who reloads the page gets a fresh answer
 *     rather than the previous tab's.
 *   • NO LOCAL VOCABULARY OF A DECISION. There is no list here of what a rationale
 *     is made of. The Engine declares which result properties are PRIMARY and
 *     this highlights exactly those; everything else is rendered generically. A
 *     rationale capability the Engine has not published therefore appears as a
 *     named absence rather than an empty screen, because the browser cannot
 *     invent the vocabulary and inventing it is the defect this workbench exists
 *     to avoid.
 *     The one exception is narrow and stated where it lives (`factKeys`): the
 *     Engine answers all six claims with ONE durable read, so each claim panel
 *     selects its own facts by the Engine's published fact keys. Nothing is
 *     renamed, completed or inferred, and the whole answer stays one disclosure
 *     away.
 *   • SELECTED AND REJECTED ARE NEVER MERGED. They are separate requests and
 *     separate panels, each scoped to its own facts of the Engine's answer, so
 *     the panel for one never shows the other. A view that merged
 *     "what we chose" with "what we rejected and why" is the failure P46's oracle
 *     is written to catch, and it is unrepresentable here.
 *
 * WHY THESE FOUR ARE FOUR
 * -----------------------
 * The distinction is the product. "The selected alternative", "the rejected
 * alternatives", "the assumptions still open" and "what would reopen this" are
 * four different claims about a real decision, and a study that reports three of
 * them has not reported the fourth. Each is therefore its own panel, sourced from
 * its own Engine capability, and a missing one is shown as missing.
 */

import { useMemo } from "react";
import { useClient } from "../../api/ClientContext";
import { buildReadRequest, inspect, type ActionPlan } from "../../api/capabilityAction";
import type { CapabilityDescriptor, CapabilityManifest } from "../../api/capabilityManifest";
import { ErrorBanner } from "../../components/ErrorBanner";
import { Empty, Panel } from "../../components/Panel";
import { useAsync } from "../../stores/useAsync";
import { ValueTree } from "../capabilities/ValueTree";
import { readBasisState } from "../basis/BasisRetrieval";
import { readCurrentness, currentnessStatement } from "../surfaces/currentness";

/**
 * Whether this claim may be satisfied by the capability named, or ONLY by a
 * durable rationale read the Engine serves.
 *
 * WHY THIS EXISTS, and the mistake it prevents
 * ---------------------------------------------
 * The previous version of this file read all six claims from `query/scenario.*`.
 * Those queries are genuine Engine reads, and a reviewer looking at a populated
 * panel would reasonably read them as the durable rationale record. They are not:
 * they answer about a SCENARIO RUN, they are not WP31's served decision-continuity
 * surface, and on the integrated Engine none of them publishes a publication
 * vocabulary — so every one of them rendered "the Engine reported no record
 * state" while still LOOKING like a retrieved rationale.
 *
 * That is exactly the failure J9 exists to prevent: a surface that appears to
 * satisfy a protocol by serving a different capability's answer. So each claim
 * declares what can satisfy it, and an unmet claim is a NAMED absence naming the
 * capability the Engine does not serve — never a substitute quietly filling the
 * gap.
 */
export type RationaleSource = "durable" | "scenario-substitute";

export interface RationaleAspect {
  readonly key: string;
  readonly title: string;
  /**
   * The capability this claim is read from, and what it may stand for.
   *
   * `durable`: this IS the claim's own Engine read, and the Engine is expected to
   * serve it. A descriptor that publishes no durable rationale capability makes
   * this claim unavailable, by name.
   *
   * `scenario-substitute`: the Engine publishes no read for this claim, and this
   * capability is displayed as what it IS — a scenario study read, labelled as
   * such — rather than as the claim. The panel states plainly that it is not the
   * durable rationale record and cannot stand in for one.
   */
  readonly capabilityId: string;
  readonly source: RationaleSource;
  /**
   * What the claim is, in one line.
   *
   * A statement about the CLAIM, not about the answer's shape. It deliberately
   * says nothing about fields, values or units: those come from the descriptor
   * the Engine published, and a sentence here that named a field would be a
   * second description of the Engine's result.
   */
  readonly why: string;
  /**
   * Which of the durable answer's facts ARE this claim, by the Engine's own fact
   * key. Durable claims only.
   *
   * One Engine capability answers all six claims, so a panel that rendered the
   * whole answer would show the selected alternative inside the "rejected" panel
   * and the rejection set inside the "selected" one — the merge this page exists
   * to make unrepresentable. Scoping each panel to its own facts is what keeps
   * the six claims six. The keys are the Engine's, matched as published, never
   * rewritten; a claim the answer carries no fact for is stated as such rather
   * than shown empty, and the complete answer stays one disclosure away.
   */
  readonly factKeys?: RegExp;
}

/**
 * The durable rationale read this workbench REQUIRES of the Engine, named.
 *
 * A single capability whose result carries the decision continuity: the selected
 * alternative, the rejected set, the open assumptions, the reopening triggers,
 * the selection basis, and the publication state of the record they belong to.
 *
 * It is a REQUIREMENT LIST, not a filter and not a denylist. Nothing is excluded
 * by naming it; if the Engine publishes it under any id the surface will show it,
 * and every other published capability stays reachable in the generic catalogue
 * exactly as before. What naming it buys is that its ABSENCE can be stated as an
 * absence rather than papered over with a study read.
 */
export const DURABLE_RATIONALE_CAPABILITY = "query/decision.continuity";

export const RATIONALE_ASPECTS: readonly RationaleAspect[] = Object.freeze([
  {
    key: "selected",
    title: "The selected alternative",
    capabilityId: DURABLE_RATIONALE_CAPABILITY,
    source: "durable",
    why: "Which option was chosen, and the reason the Engine gives for it.",
    factKeys: /\.selected_alternative\.|^rationale\.study\.\d+\.decision\.\d+\.selection_reason$/,
  },
  {
    key: "rejected",
    title: "The rejected alternatives",
    capabilityId: DURABLE_RATIONALE_CAPABILITY,
    source: "durable",
    why: "Which options were not chosen, and the stated constraint each failed. Never merged with the selection.",
    factKeys: /\.(rejected|deferred)\.\d+\.|\.rejection_set\.|\.(rejected|deferred)_count$|^rationale\.(rejected|deferred)\.count$/,
  },
  {
    key: "assumptions",
    title: "The open assumptions",
    capabilityId: DURABLE_RATIONALE_CAPABILITY,
    source: "durable",
    why: "What the study is still resting on. An answer is only as good as the assumptions under it.",
    factKeys: /\.open_assumption\.|^rationale\.open_assumption\.count$/,
  },
  {
    key: "reopening",
    title: "The reopening triggers",
    capabilityId: DURABLE_RATIONALE_CAPABILITY,
    source: "durable",
    why: "What would make this decision be looked at again, and by what it would be judged stale.",
    factKeys: /\.(reopening_condition|reopening_status)$|\.trigger_evidence(\.\d+|_count)$/,
  },
  {
    key: "basis",
    title: "The selection basis",
    capabilityId: DURABLE_RATIONALE_CAPABILITY,
    source: "durable",
    why: "The declared constraints, options and schedules the selection was made against.",
    factKeys: /\.selection_basis\.|\.selected_evaluation\./,
  },
  {
    key: "state",
    title: "Draft or published",
    capabilityId: DURABLE_RATIONALE_CAPABILITY,
    source: "durable",
    why: "Whether this record is canonical yet, read from the Engine's own vocabulary and never guessed.",
    factKeys: /^rationale\.study\.\d+\.(study\.(record_status|published|not_canonical_reason)|canonical_state|currentness|currentness_reason|revision\.(state|recorded)|decision\.\d+\.(record_status|decision_state|currentness|currentness_reason))$/,
  },
]);

/**
 * What the Engine DOES publish that is adjacent to a rationale claim, displayed
 * as itself.
 *
 * These are real reads and they are worth showing — a reviewer looking for why an
 * option was chosen is better served by an answered scenario query than by an
 * empty panel. They are shown under their own names with an explicit statement
 * that they are SCENARIO STUDY reads, that they are not the durable rationale
 * record, and that they cannot satisfy a `durable` claim.
 *
 * This is the whole difference between an honest substitute and a dishonest
 * one: the substitute is labelled with what it actually is.
 */
export const SCENARIO_CONTEXT_READS: readonly RationaleAspect[] = Object.freeze([
  {
    key: "scenario-explanation",
    title: "Scenario study explanation",
    capabilityId: "query/scenario.explain",
    source: "scenario-substitute",
    why: "The Engine's explanation of a scenario RUN. This is a study read, not the decision record the claims above require.",
  },
  {
    key: "scenario-comparison",
    title: "Scenario study comparison",
    capabilityId: "query/scenario.compare",
    source: "scenario-substitute",
    why: "A comparison of two scenario runs. Adjacent to a rationale claim; not a substitute for the durable record.",
  },
  {
    key: "scenario-provenance",
    title: "Scenario study provenance",
    capabilityId: "query/scenario.provenance",
    source: "scenario-substitute",
    why: "Where a scenario run's inputs and outputs came from. Provenance of a study, not publication state of a record.",
  },
  {
    key: "scenario-definitions",
    title: "Scenario study definitions",
    capabilityId: "query/scenario.definitions",
    source: "scenario-substitute",
    why: "The declared constraints, options and schedules of a study definition.",
  },
]);

/**
 * Resolve the aspects against the manifest.
 *
 * An aspect the Engine does not publish is a NAMED absence. The four-way split is
 * only meaningful if a missing aspect is visible: a rationale view that silently
 * dropped "rejected alternatives" because the Engine had not published it would
 * look exactly like a study with no rejected alternatives, and those are very
 * different facts.
 */
export function resolveAspects(
  manifest: CapabilityManifest,
  aspects: readonly RationaleAspect[] = RATIONALE_ASPECTS,
): readonly {
  aspect: RationaleAspect;
  descriptor: CapabilityDescriptor | undefined;
  plan: ActionPlan | undefined;
  missing: string | undefined;
}[] {
  return aspects.map(aspect => {
    const descriptor = [...manifest.capabilities, ...manifest.projections].find(d => d.id === aspect.capabilityId);
    if (descriptor === undefined) {
      return { aspect, descriptor: undefined, plan: undefined, missing: aspect.capabilityId };
    }
    const resolution = inspect(manifest, descriptor);
    return {
      aspect,
      descriptor,
      plan: resolution.addressable ? resolution.plan : undefined,
      missing: resolution.addressable ? undefined : `a route this browser has for ${aspect.capabilityId}`,
    };
  });
}

/**
 * What the rationale surface can actually show, stated as a fact about the Engine.
 *
 * `durableAvailable` is the load-bearing half and it is computed, never asserted:
 * it is true only when the manifest publishes the capability the durable claims
 * require. The page says so in words, and P46's oracle reads this rather than
 * counting panels — because a rationale page whose every panel is a scenario
 * study read LOOKS complete and is not.
 */
export function rationaleAvailability(manifest: CapabilityManifest): {
  durableAvailable: boolean;
  durableCapability: string;
  servedSubstitutes: readonly string[];
} {
  const published = new Set([...manifest.capabilities, ...manifest.projections].map(d => d.id));
  return {
    durableAvailable: published.has(DURABLE_RATIONALE_CAPABILITY),
    durableCapability: DURABLE_RATIONALE_CAPABILITY,
    servedSubstitutes: SCENARIO_CONTEXT_READS
      .filter(read => published.has(read.capabilityId))
      .map(read => read.capabilityId),
  };
}

/**
 * The facts of one claim, in the Engine's order, keys and values untouched.
 *
 * An answer that is not a query result, or carries no `facts` array, yields no
 * facts for the claim — which the panel states — rather than a guess.
 */
export function factsOfClaim(raw: unknown, keys: RegExp): readonly { key: string; value: unknown }[] {
  const facts = (raw as { facts?: unknown } | undefined)?.facts;
  if (!Array.isArray(facts)) return [];
  return facts.filter((fact): fact is { key: string; value: unknown } =>
    typeof fact === "object" && fact !== null && typeof (fact as { key?: unknown }).key === "string"
    && keys.test((fact as { key: string }).key));
}

function formatFactValue(value: unknown): string {
  return typeof value === "string" ? value : JSON.stringify(value);
}

function RationaleRead({ projectId, aspect, descriptor, plan }: {
  projectId: string;
  aspect: RationaleAspect;
  descriptor: CapabilityDescriptor;
  plan: ActionPlan;
}): JSX.Element {
  const client = useClient();
  // No cache, no dependency on anything but this mount.
  const retrieved = useAsync(
    async () => client.capabilityRead(projectId, buildReadRequest(plan, {}).body),
    [client, plan, projectId],
  );
  const currentness = useMemo(
    () => (retrieved.data === undefined ? undefined : readCurrentness(descriptor, retrieved.data)),
    [descriptor, retrieved.data],
  );
  const recordState = useMemo(() => readBasisState(descriptor, retrieved.data), [descriptor, retrieved.data]);
  const claimFacts = useMemo(
    () => (aspect.factKeys === undefined ? undefined : factsOfClaim(retrieved.data, aspect.factKeys)),
    [aspect.factKeys, retrieved.data],
  );

  return (
    // The wrapper carries the identity hooks so a real browser test can address
    // ONE claim of the rationale without matching on heading text.
    <div data-aspect={aspect.key} data-capability={descriptor.id} data-source={aspect.source}>
    <Panel title={aspect.title} testId="rationale-aspect">
      <p className="muted">{aspect.why}</p>
      {aspect.source === "scenario-substitute" && (
        // THE LABELLING THAT MAKES A SUBSTITUTE HONEST. Without this sentence a
        // populated scenario panel reads as a retrieved rationale record, which
        // is the exact substitution J9 forbids.
        <p className="bad" data-stage="rationale-not-durable">
          <strong>This is not the durable rationale record.</strong> It is a scenario study read. It is shown because
          it is a real Engine answer about a study, and it is labelled so it cannot be mistaken for the decision record
          the claims above require. It does not satisfy any of them.
        </p>
      )}
      <dl className="facts compact">
        <dt>Capability</dt><dd><code>{descriptor.id}</code></dd>
        <dt>Stands for</dt>
        <dd>{aspect.source === "durable" ? "The claim it names" : "A scenario study, not the decision record"}</dd>
        <dt>Record state</dt>
        <dd data-stage="rationale-record-state">
          {/* An unreported state is rendered as unreported. A draft banner for a
              record the Engine never described would be a claim about a real
              engineering record that nobody made. */}
          {recordState.recordState ?? <span className="bad">the Engine reported no record state</span>}
        </dd>
        <dt>Publication</dt>
        <dd data-stage="rationale-publication">
          {recordState.publication === "published" ? "Published"
            : recordState.publication === "draft" ? "Draft — not yet canonical"
            : "Unreported — this browser will not guess"}
        </dd>
        {recordState.canonicalRevision !== undefined && (
          <><dt>Canonical revision</dt><dd className="request-id">{recordState.canonicalRevision}</dd></>
        )}
      </dl>
      <ErrorBanner error={retrieved.error} onDismiss={retrieved.reload} />
      {retrieved.loading ? <Empty>Retrieving from the Engine…</Empty> : null}
      {currentness !== undefined && (
        <div className="currentness" data-stage="stage-currentness" data-status-reading={currentness.status} data-status={currentness.statusValue ?? ""}>
          <p className={currentnessStatement(currentness).settled ? "muted" : "bad"}>{currentnessStatement(currentness).text}</p>
        </div>
      )}
      {retrieved.data !== undefined && claimFacts !== undefined && (
        <div data-stage="rationale-claim-facts" data-count={claimFacts.length}>
          <h3>This claim, as the Engine recorded it</h3>
          {claimFacts.length === 0 ? (
            <p className="bad">
              The Engine&apos;s answer carries no fact for this claim. It is stated here rather than shown empty.
            </p>
          ) : (
            <dl className="facts compact">
              {claimFacts.map(fact => (
                <div key={fact.key} className="fact-row">
                  <dt><code>{fact.key}</code></dt>
                  <dd data-fact-key={fact.key}>{formatFactValue(fact.value)}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      )}
      {retrieved.data !== undefined && (claimFacts !== undefined ? (
        <details>
          <summary>The complete Engine answer</summary>
          <ValueTree
            value={retrieved.data}
            schema={descriptor.resultSchema}
            highlight={descriptor.presentation.primaryFields}
          />
        </details>
      ) : (
        <>
          <h3>What the Engine returned</h3>
          <ValueTree
            value={retrieved.data}
            schema={descriptor.resultSchema}
            highlight={descriptor.presentation.primaryFields}
          />
        </>
      ))}
      <p className="muted">
        Retrieved from the Engine on this open. Nothing here is remembered between visits, and nothing here was written
        by this browser.
      </p>
    </Panel>
    </div>
  );
}

export function RationalePage({ manifest, projectId }: { manifest: CapabilityManifest; projectId: string }): JSX.Element {
  const aspects = useMemo(() => resolveAspects(manifest), [manifest]);
  const served = aspects.filter(entry => entry.plan !== undefined);
  const absent = aspects.filter(entry => entry.plan === undefined);
  const availability = useMemo(() => rationaleAvailability(manifest), [manifest]);
  const substitutes = useMemo(() => resolveAspects(manifest, SCENARIO_CONTEXT_READS), [manifest]);

  return (
    <div className="page">
      <header className="page-head">
        <h1>Rationale</h1>
        <p className="muted">
          Six separate claims about a decision, each read from the Engine on this open. Selected and rejected are
          never merged, and a claim the Engine has not published is named rather than omitted.
        </p>
      </header>

      {/* THE HONEST HEADER. Whether the durable rationale record is retrievable at
          all is a fact about the Engine's manifest, and it is stated first, in
          words, before any panel — so a reviewer scrolling this page cannot
          reach the bottom believing the six claims above were answered. */}
      <Panel
        title="Can this page show the decision record?"
        tone={availability.durableAvailable ? "default" : "warn"}
        testId="rationale-availability"
      >
        <p
          className={availability.durableAvailable ? "muted" : "bad"}
          data-stage="rationale-durable-available"
          data-available={availability.durableAvailable ? "true" : "false"}
        >
          {availability.durableAvailable ? (
            <>The Engine publishes <code data-stage="rationale-durable-capability">{availability.durableCapability}</code>, so every claim below is read from the
              durable rationale record.</>
          ) : (
            <>
              <strong>No. The Engine&apos;s manifest does not publish a durable rationale read.</strong> This
              workbench requires <code data-stage="rationale-durable-capability">{availability.durableCapability}</code>
              , and no such capability is published. All six claims below are therefore unavailable, and they are
              named individually rather than shown as empty.
            </>
          )}
        </p>
        <p className="muted">
          {availability.durableAvailable
            ? "Scenario study reads are shown separately below and are labelled as such."
            : "Scenario study reads this Engine does publish are shown below, labelled as scenario reads. They are "
              + "real answers about studies. They are not the decision record, and they do not stand in for one."}
        </p>
        {availability.servedSubstitutes.length > 0 && (
          <p className="muted" data-stage="rationale-substitutes">
            Adjacent reads the Engine does serve:{" "}
            {availability.servedSubstitutes.map(id => <code key={id}>{id}</code>).reduce<React.ReactNode[]>(
              (acc, node, index) => (index === 0 ? [node] : [...acc, ", ", node]),
              [],
            )}
          </p>
        )}
      </Panel>

      {served.map(entry => (
        <RationaleRead
          key={entry.aspect.key}
          projectId={projectId}
          aspect={entry.aspect}
          descriptor={entry.descriptor!}
          plan={entry.plan!}
        />
      ))}
      {absent.map(entry => (
        <div key={entry.aspect.key} data-aspect={entry.aspect.key} data-capability={entry.aspect.capabilityId} data-source={entry.aspect.source}>
        <Panel title={entry.aspect.title} tone="warn" testId="rationale-absent">
          <p className="bad">
            <strong>Not retrievable.</strong> This workbench needs{" "}
            <code data-stage="absent-capability">{entry.aspect.capabilityId}</code> for this claim, and the
            Engine&apos;s manifest does not publish a way to reach it.
          </p>
          <p className="muted">{entry.aspect.why}</p>
          <p className="muted">
            It is not shown as empty. An empty panel here would be indistinguishable from a study with no rejected
            alternatives, and those are very different facts.
          </p>
        </Panel>
        </div>
      ))}

      <h2 data-stage="rationale-context-head">Adjacent reads the Engine does serve</h2>
      <p className="muted">
        These are genuine Engine answers about scenario studies. Each is labelled with what it is, because the whole
        failure J9 guards against is a study read quietly presented as a rationale record.
      </p>
      {substitutes.map(entry => entry.plan === undefined ? (
        <div key={entry.aspect.key} data-aspect={entry.aspect.key} data-capability={entry.aspect.capabilityId} data-source={entry.aspect.source}>
          <Panel title={entry.aspect.title} tone="warn" testId="rationale-substitute-absent">
            <p className="bad">
              <strong>Not published either.</strong>{" "}
              <code data-stage="absent-capability">{entry.aspect.capabilityId}</code> is not in this Engine&apos;s
              manifest.
            </p>
          </Panel>
        </div>
      ) : (
        <RationaleRead
          key={entry.aspect.key}
          projectId={projectId}
          aspect={entry.aspect}
          descriptor={entry.descriptor!}
          plan={entry.plan!}
        />
      ))}
    </div>
  );
}
